import React, { useCallback, useMemo, useState } from "react";

import {
  Collapse,
  Divider,
  Drawer as MUIDrawer,
  useTheme,
} from "@mui/material/";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export interface DrawerMenuItem {
  label: string;
  path?: string;
  onClick?: () => void;
  childItems?: DrawerMenuItem[];
}

interface DrawerProps {
  title?: string | JSX.Element;
  open: boolean;
  onClose: () => void;
  drawerList: DrawerMenuItem[];
}

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  drawerList,
  title = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [nestedDrawerOpen, setNestedDrawerOpen] = useState(() => {
    return drawerList
      .filter(
        ({ childItems }) =>
          childItems && Array.isArray(childItems) && childItems.length > 0,
      )
      .map(({ label }) => ({ label, open: false }));
  });

  const handleToggleTools = (targetLabel: string) => {
    setNestedDrawerOpen((prev) => [
      ...prev.filter(({ label }) => label !== targetLabel),
      {
        label: targetLabel,
        open: !prev.find(({ label }) => label === targetLabel)?.open,
      },
    ]);
  };

  const getMenuButtonToggleIcon = useCallback(
    (hasChildElements: boolean, open: boolean) => {
      if (!hasChildElements) {
        return null;
      }

      return open ? <ExpandLess /> : <ExpandMore />;
    },
    [],
  );

  const getChildMenuItems = useCallback(
    (
      hasChildElements: boolean,
      open: boolean,
      childItems: DrawerMenuItem[],
    ) => {
      if (!hasChildElements) {
        return null;
      }

      return (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {childItems.map(
              ({
                label: childLabel,
                path: childPath,
                onClick: onChildClick,
              }) => {
                const handleChildClick = () => {
                  if (childPath) {
                    navigate(childPath);
                  } else if (onChildClick) {
                    onChildClick();
                  }
                  onClose();
                };

                return (
                  <ListItemButton
                    key={JSON.stringify({ childLabel, childPath })}
                    style={{ paddingLeft: theme.spacing(4) }}
                    onClick={handleChildClick}
                  >
                    <ListItemText primary={childLabel} />
                  </ListItemButton>
                );
              },
            )}
          </List>
        </Collapse>
      );
    },
    [navigate, onClose, theme],
  );

  const decoratedDrawerList = useMemo(
    () => (
      <Box role="presentation">
        {(() => {
          if (!title) {
            return;
          }

          return (
            <>
              {title}
              <Divider
                sx={{
                  borderColor: theme.palette.common.white,
                  marginTop: theme.spacing(4),
                }}
              />
            </>
          );
        })()}

        <List
          sx={{ width: "100%" }}
          component="nav"
          aria-labelledby="nested-list-subheader"
        >
          {drawerList.map(({ label, path, onClick, childItems }) => {
            const hasChildElements =
              childItems && Array.isArray(childItems) && childItems.length > 0;

            const handleMenuButtonClick = () => {
              if (path) {
                navigate(path);
              } else if (onClick) {
                onClick();
              }

              onClose();
            };

            const isMenuOpen = nestedDrawerOpen.find(
              ({ label: toggleLabel }) => toggleLabel === label,
            )?.open;

            return (
              <div key={JSON.stringify({ label, path })}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() =>
                      hasChildElements
                        ? handleToggleTools(label)
                        : handleMenuButtonClick()
                    }
                  >
                    <ListItemText primary={label} />

                    {getMenuButtonToggleIcon(!!hasChildElements, !!isMenuOpen)}
                  </ListItemButton>
                </ListItem>

                {childItems &&
                  getChildMenuItems(
                    !!hasChildElements,
                    !!isMenuOpen,
                    childItems,
                  )}
              </div>
            );
          })}
        </List>
      </Box>
    ),
    [
      drawerList,
      getChildMenuItems,
      getMenuButtonToggleIcon,
      nestedDrawerOpen,
      onClose,
      navigate,
      title,
      theme,
    ],
  );

  return (
    <MUIDrawer open={open} onClose={onClose}>
      <div style={{ padding: "30px" }}>{decoratedDrawerList}</div>
    </MUIDrawer>
  );
};

export default Drawer;
